import { useRef, useState } from "react";
import Webcam from "react-webcam";
import { Camera, RotateCcw, Upload } from "lucide-react";
import { api } from "../../../lib/api";
import { dataUrlToBlob, fileToDataUrl } from "../../../lib/fileUtils";
import { Button } from "../../../components/ui/button";
import { StepHeader } from "../../../components/kiosk/StepHeader";
import { StepProgress } from "../../../components/kiosk/StepProgress";

export default function PhotoIdStep({ visitId, offline = false, onNext }) {
  const webcamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [webcamError, setWebcamError] = useState(false);

  const capture = () => {
    const shot = webcamRef.current?.getScreenshot();
    if (shot) setPhoto(shot);
  };

  const continueNext = async () => {
    if (offline) {
      onNext({
        photo_base64: photo || null,
        id_file_base64: idFile ? await fileToDataUrl(idFile) : null,
        id_file_name: idFile?.name || null,
      });
      return;
    }
    setUploading(true);
    try {
      if (photo) {
        const fd = new FormData();
        fd.append("file", dataUrlToBlob(photo), "photo.jpg");
        await api.post(`/visits/${visitId}/upload-photo`, fd);
      }
      if (idFile) {
        const fd2 = new FormData();
        fd2.append("file", idFile);
        await api.post(`/visits/${visitId}/upload-id`, fd2);
      }
      onNext();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="checkin-photo-step">
      <StepProgress current="photo" />
      <StepHeader icon={Camera} title="Photo & ID Capture" subtitle="Take a visitor photo and optionally attach an ID document" />
      <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 sm:p-8 space-y-4">
        <div className="border border-slate-200 bg-slate-50 aspect-video flex items-center justify-center overflow-hidden rounded-md">
          {photo ? (
            <img src={photo} alt="Captured visitor" className="w-full h-full object-cover" data-testid="captured-photo-preview" />
          ) : webcamError ? (
            <p className="text-slate-400 text-sm p-6 text-center" data-testid="webcam-error">
              Camera unavailable — you may skip photo capture
            </p>
          ) : (
            <Webcam
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="w-full h-full object-cover"
              onUserMediaError={() => setWebcamError(true)}
              data-testid="webcam-view"
            />
          )}
        </div>
        <div className="flex gap-3">
          {photo ? (
            <Button onClick={() => setPhoto(null)} data-testid="retake-photo-button" variant="outline" className="flex-1 h-12 rounded-sm border-slate-300 text-slate-700 gap-2">
              <RotateCcw size={18} /> Retake
            </Button>
          ) : (
            <Button onClick={capture} disabled={webcamError} data-testid="capture-photo-button" className="flex-1 h-12 rounded-sm bg-brand-cyan text-white hover:bg-brand-cyan-dark font-bold uppercase gap-2">
              <Camera size={18} /> Capture Photo
            </Button>
          )}
        </div>
        <label
          className="flex items-center justify-center gap-2 h-14 border border-dashed border-slate-300 cursor-pointer text-slate-500 hover:text-brand-navy hover:border-slate-400 rounded-md"
          data-testid="id-upload-label"
        >
          <Upload size={18} />
          {idFile ? idFile.name : "Upload ID / document (optional)"}
          <input type="file" accept="image/*,.pdf" data-testid="id-upload-input" className="hidden" onChange={(e) => setIdFile(e.target.files?.[0] || null)} />
        </label>
        <Button onClick={continueNext} disabled={uploading} data-testid="photo-step-continue" className="w-full h-16 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-lg">
          {uploading ? "Uploading..." : "Continue"}
        </Button>
      </div>
    </div>
  );
}
